import { NavLink, Route, Routes } from 'react-router-dom';
import NotFound from '@/pages/NotFound';
import { useSession } from '@/lib/session';
import Overview from './tabs/Overview';
import Brief from './tabs/Brief';
import Radar from './tabs/Radar';
import Companies from './tabs/Companies';
import CompanyDossier from './tabs/CompanyDossier';
import Assets from './tabs/Assets';
import Distress from './tabs/Distress';
import Deals from './tabs/Deals';
import Papers from './tabs/Papers';
import Discovery from './tabs/Discovery';
import Sources from './tabs/Sources';
import Prompts from './tabs/Prompts';
import Rubric from './tabs/Rubric';
import Debug from './tabs/Debug';

// M&A Radar — the acquisition-targets module, rendered inside the portal shell at /mna
// (same in-shell tab-nav pattern as WwdApp). Phase A serves typed fixtures through
// lib/api; A6 flipped USE_FIXTURES and the same tabs read the real MNA.Api through the
// portal BFF (/api/mna/**). Debug is admin-only, exactly like WWD's. Distress went live
// in Phase B; Phase C landed Deals (comparable transactions), the weekly Brief, and the
// Overview alerts strip. Phase D added the dossier workflow panel (stage + notes),
// dossier export, and the admin-only Rubric tab (thesis-rubric editor) — Rubric sits
// beside Debug rather than inside it: Debug is ops plumbing, the rubric is domain config.
const baseTabs = [
  { to: '/mna', label: 'Overview', end: true },
  { to: '/mna/brief', label: 'Brief', end: false },
  { to: '/mna/radar', label: 'Radar', end: false },
  { to: '/mna/companies', label: 'Companies', end: false },
  { to: '/mna/assets', label: 'Assets', end: false },
  { to: '/mna/distress', label: 'Distress', end: false },
  { to: '/mna/deals', label: 'Deals', end: false },
  { to: '/mna/papers', label: 'Papers', end: false },
  { to: '/mna/discovery', label: 'Discovery', end: false },
  { to: '/mna/sources', label: 'Sources', end: false },
  { to: '/mna/prompts', label: 'Prompts', end: false },
];

export default function MnaApp() {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;

  const tabs = [
    ...baseTabs,
    ...(isAdmin
      ? [
          { to: '/mna/rubric', label: 'Rubric', end: false },
          { to: '/mna/debug', label: 'Debug', end: false },
        ]
      : []),
  ];

  return (
    <div>
      <nav className="mb-6 flex flex-wrap gap-1 border-b border-border" aria-label="M&A Radar">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              'px-3 py-2 text-sm font-(family-name:--font-ui) border-b-2 -mb-px transition-colors ' +
              (isActive ? 'border-accent text-text' : 'border-transparent text-muted hover:text-text')
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="brief" element={<Brief />} />
        <Route path="radar" element={<Radar />} />
        <Route path="companies" element={<Companies />} />
        {/* Dossiers are deep, deep-linkable objects — a route view, not a modal. */}
        <Route path="companies/:slug" element={<CompanyDossier />} />
        <Route path="assets" element={<Assets />} />
        <Route path="distress" element={<Distress />} />
        <Route path="deals" element={<Deals />} />
        <Route path="papers" element={<Papers />} />
        <Route path="discovery" element={<Discovery />} />
        <Route path="sources" element={<Sources />} />
        <Route path="prompts" element={<Prompts />} />
        <Route path="rubric" element={<Rubric />} />
        <Route path="debug" element={<Debug />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}
